import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { adminApi, fileUrl } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import { useEvents } from "../../lib/events";
import AdminLayout from "../../components/app/AdminLayout";
import { Loader } from "./Dashboard";
import { ActorSearchList, ModalShell } from "../../components/app/AssignModals";
import { TempCoverPanel } from "../../components/app/TempCoverPanel";
import { Printer, Loader2, Trash2, User, ArrowUpDown, GripVertical, ArrowUp, ArrowDown, Check, ClipboardList } from "lucide-react";

const ageFromDob = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d)) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 31557600000));
};

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
// "Friday 2nd" — weekday + ordinal day-of-month (locale-safe date parse).
const formatDayOrdinal = (dateStr) => {
  if (!dateStr) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return `${d.toLocaleDateString("en-GB", { weekday: "long" })} ${ordinal(d.getDate())}`;
};

const COLORS = {
  signed_in: { bg: "#10B981", text: "#000", label: "IN" },
  signed_out: { bg: "#3B82F6", text: "#FFF", label: "OUT" },
  assigned: { bg: "#3F3F46", text: "#FFF", label: "ASSIGNED" },
  unfilled: { bg: "#EF4444", text: "#FFF", label: "—" },
};
const STAGE_COLORS = {
  preliminary: { bg: "#78350F", text: "#FDE68A" },
  invited: { bg: "#1E3A8A", text: "#BFDBFE" },
  confirmed: { bg: "#166534", text: "#BBF7D0" },
  temp: { bg: "#155E75", text: "#CFFAFE" },
};

export default function Roster() {
  const { formatDate } = useSettings();
  const { isArchived } = useEvents();
  const [data, setData] = useState(null);
  const [cell, setCell] = useState(null); // { role, showDate, cell }
  const [avail, setAvail] = useState(null); // available actors for the open cell
  const [sendEmail, setSendEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  // Manual sort mode (Update 21)
  const [sortMode, setSortMode] = useState(false);
  const [order, setOrder] = useState([]);
  const [savingSort, setSavingSort] = useState(false);
  const [dragId, setDragId] = useState(null);
  // Update 27 Change 10C: arrange rows by roster order or role-builder order.
  const [rosterSort, setRosterSort] = useState("roster");
  // 2-second hover preview popover
  const [hover, setHover] = useState(null); // { actor, x, y }
  const [hidePast, setHidePast] = useState(() => localStorage.getItem("roster_hide_past") === "1");
  const toggleHidePast = (v) => { setHidePast(v); localStorage.setItem("roster_hide_past", v ? "1" : "0"); };
  const hoverTimer = useRef(null);

  const startHover = (actorId, el) => {
    const actor = data?.actors?.[actorId];
    if (!actor) return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      const r = el.getBoundingClientRect();
      setHover({ actor, x: r.left + r.width / 2, y: r.bottom + 8 });
    }, 1000);
  };
  const endHover = () => { clearTimeout(hoverTimer.current); setHover(null); };
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const load = () => adminApi.get("/admin/roster", { params: { sort_by: rosterSort } }).then(({ data }) => setData(data));
  useEffect(() => { load(); }, [rosterSort]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCell = async (role, showDate, c) => {
    if (isArchived) return;
    setCell({ role, showDate, cell: c });
    setAvail(null);
    setSendEmail(false);
    const { data: r } = await adminApi.get(`/admin/roster/available-actors`, { params: { show_date_id: showDate.id, role_id: role.id } });
    setAvail(r.items || []);
  };
  const closeCell = () => { setCell(null); setAvail(null); };

  const assign = async (actor) => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/roster/assign`, { role_id: cell.role.id, show_date_id: cell.showDate.id, actor_id: actor.id, send_email: sendEmail });
      closeCell(); await load();
    } finally { setBusy(false); }
  };
  const clearCell = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/roster/unassign`, { role_id: cell.role.id, show_date_id: cell.showDate.id });
      closeCell(); await load();
    } finally { setBusy(false); }
  };

  const enterSort = () => { setOrder((data?.rows || []).map((r) => r.role.id)); setSortMode(true); };
  const moveRow = (id, dir) => setOrder((o) => {
    const a = [...o]; const i = a.indexOf(id); const j = i + dir;
    if (j < 0 || j >= a.length) return a;
    [a[i], a[j]] = [a[j], a[i]]; return a;
  });
  const dropOn = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    setOrder((o) => { const a = [...o]; const from = a.indexOf(dragId); const to = a.indexOf(targetId); a.splice(from, 1); a.splice(to, 0, dragId); return a; });
    setDragId(null);
  };
  const saveSort = async () => {
    setSavingSort(true);
    try {
      await adminApi.post("/admin/roster/reorder", { ordered_role_ids: order });
      setSortMode(false); await load();
    } finally { setSavingSort(false); }
  };

  if (!data) return <AdminLayout title="Roster"><Loader/></AdminLayout>;

  const rowsById = Object.fromEntries((data.rows || []).map((r) => [r.role.id, r]));
  const displayRows = sortMode ? order.map((id) => rowsById[id]).filter(Boolean) : data.rows;
  const todayYmd = new Date().toLocaleDateString("en-CA");
  const visIdx = data.show_dates.map((sd, i) => i).filter((i) => !hidePast || (data.show_dates[i].date || "") >= todayYmd);
  const pastCount = data.show_dates.filter((sd) => (sd.date || "") < todayYmd).length;

  return (
    <AdminLayout title="Roster" subtitle="Every open date against every named role — click a cell to assign or swap"
      actions={<div className="flex items-center gap-2">
        <label className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] cursor-pointer hover:border-blood" data-testid="roster-hide-past">
          <input type="checkbox" checked={hidePast} onChange={(e) => toggleHidePast(e.target.checked)} className="accent-blood" data-testid="roster-hide-past-checkbox" />
          Hide past dates{pastCount ? ` (${pastCount})` : ""}
        </label>
        <select value={rosterSort} onChange={(e) => setRosterSort(e.target.value)} disabled={sortMode} data-testid="roster-sort-by"
          className="px-3 py-2 text-xs uppercase tracking-wider bg-[#0A0A0A] border border-[#262626] text-[#A19D94] focus:outline-none focus:border-blood disabled:opacity-50">
          <option value="roster">Sort by: Roster order</option>
          <option value="builder">Sort by: Role builder order</option>
        </select>
        <a href="/signin" target="_blank" rel="noopener noreferrer" data-testid="roster-signin-sheet-link"
          className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-blood hover:text-blood"><ClipboardList size={14} /> Sign-In Sheet</a>
        {!isArchived && (sortMode ? (
          <button onClick={saveSort} disabled={savingSort} data-testid="roster-sort-done" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-[#16A34A] text-white hover:bg-[#15803D] disabled:opacity-50">
            {savingSort ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Done
          </button>
        ) : (
          <button onClick={enterSort} data-testid="roster-sort-toggle" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-blood hover:text-blood"><ArrowUpDown size={14} /> Sort</button>
        ))}
        <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-blood hover:text-blood"><Printer size={14} /> Print View</button>
      </div>}>
      {sortMode && <div className="mb-4 px-3 py-2 text-xs text-[#F59E0B] border border-[#F59E0B]/40 bg-[#F59E0B]/10" data-testid="roster-sort-banner">Sort mode — drag the handle or use the arrows to reorder positions, then click Done to save. This order applies to the roster, print view and tablet sign-in.</div>}
      <div className="mb-4 flex flex-wrap gap-3 text-xs" data-testid="roster-legend">
        <Legend color="#EF4444" label="Unfilled" />
        <Legend color="#78350F" label="Preliminary" />
        <Legend color="#1E3A8A" label="Invited" />
        <Legend color="#166534" label="Confirmed" />
        <Legend color="#155E75" label="Temp Cover" />
        <Legend color="#10B981" label="Signed In" />
        <Legend color="#3B82F6" label="Signed Out" />
      </div>

      <div className="border border-[#1a1a1a] bg-[#0A0A0A] overflow-auto max-h-[calc(100vh-16rem)] print:max-h-none print:overflow-visible" data-testid="roster-scroll">
        <table className="text-xs min-w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-30 bg-[#0A0A0A] px-3 py-3 label-mini text-left min-w-[180px] border-b border-r border-[#1a1a1a]">Role</th>
              {visIdx.map((i) => data.show_dates[i]).map((sd) => (
                <th key={sd.id} className="sticky top-0 z-20 bg-[#0A0A0A] px-2 py-3 label-mini text-center min-w-[110px] border-b border-l border-[#1a1a1a]">
                  <div>{formatDayOrdinal(sd.date)}</div>
                  <div className="text-[9px] text-[#6B6862] mt-0.5">{sd.name?.slice(0,12)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={visIdx.length+1} className="py-12 text-center text-[#6B6862]">No roles defined yet. Add roles in <strong>Role Builder</strong>.</td></tr>}
            {displayRows.map((row) => (
              <tr key={row.role.id} className="border-b border-[#1a1a1a]"
                draggable={sortMode}
                onDragStart={() => sortMode && setDragId(row.role.id)}
                onDragOver={(e) => sortMode && e.preventDefault()}
                onDrop={() => sortMode && dropOn(row.role.id)}>
                <td className="sticky left-0 z-10 bg-[#0A0A0A] px-3 py-2 border-b border-r border-[#1a1a1a]">
                  <div className="flex items-center gap-2">
                    {sortMode && (
                      <div className="flex items-center gap-1 shrink-0" data-testid={`roster-sort-controls-${row.role.id}`}>
                        <GripVertical size={14} className="text-[#6B6862] cursor-grab" />
                        <div className="flex flex-col">
                          <button onClick={() => moveRow(row.role.id, -1)} data-testid={`roster-move-up-${row.role.id}`} className="text-[#6B6862] hover:text-[#F2EBE1] leading-none"><ArrowUp size={12} /></button>
                          <button onClick={() => moveRow(row.role.id, 1)} data-testid={`roster-move-down-${row.role.id}`} className="text-[#6B6862] hover:text-[#F2EBE1] leading-none"><ArrowDown size={12} /></button>
                        </div>
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-display uppercase">{row.role.name}</div>
                      {row.role.placement && <div className="text-[10px] text-[#6B6862]">{row.role.placement}</div>}
                    </div>
                  </div>
                </td>
                {visIdx.map((i) => {
                  const c = row.cells[i];
                  const col = COLORS[c.state] || COLORS.unfilled;
                  const stageCol = c.state === "assigned" && c.stage ? STAGE_COLORS[c.stage] : null;
                  const cellCol = stageCol || col;
                  const sd = data.show_dates[i];
                  const actor = c.actor_id ? data.actors?.[c.actor_id] : null;
                  return (
                    <td key={sd.id} className="px-1.5 py-1.5 text-center border-b border-l border-[#1a1a1a]">
                      <button
                        onClick={() => !sortMode && openCell(row.role, sd, c)}
                        onMouseEnter={(e) => !sortMode && c.actor_id && startHover(c.actor_id, e.currentTarget)}
                        onMouseLeave={endHover}
                        disabled={isArchived || sortMode}
                        data-testid={`roster-cell-${row.role.id}-${sd.id}`}
                        className="w-full px-2 py-1.5 text-[10px] font-mono uppercase tracking-wider leading-tight transition-opacity hover:opacity-80 disabled:cursor-default"
                        style={{ background: cellCol.bg, color: cellCol.text }}>
                        {actor ? (
                          <span className="block">
                            <span className="block">{actor.first_name || (c.actor_name || "").split(" ")[0]}</span>
                            <span className="block">{actor.last_name || (c.actor_name || "").split(" ").slice(1).join(" ")}</span>
                          </span>
                        ) : c.actor_name ? c.actor_name : col.label}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cell && (
        <ModalShell
          testid="roster-cell-modal"
          title={cell.cell.actor_name ? "Swap Actor" : "Assign Actor"}
          subtitle={`${cell.role.name} · ${formatDate(cell.showDate.date)}`}
          onClose={closeCell}
        >
          {cell.cell.actor_name && (
            <div className="flex items-center justify-between gap-2 mb-4 px-3 py-2.5 border border-blood/40 bg-blood/5">
              <div className="text-sm">Currently: <span className="text-blood font-medium" data-testid="roster-current-actor">{cell.cell.actor_name}</span></div>
              <button onClick={clearCell} disabled={busy} data-testid="roster-clear-cell" className="text-xs uppercase tracking-wider text-[#6B6862] hover:text-blood inline-flex items-center gap-1.5"><Trash2 size={12} /> Clear</button>
            </div>
          )}
          {avail === null ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-blood" size={22} /></div>
          ) : (
            <>
              <p className="text-xs text-[#6B6862] mb-2">Available actors for this night (not already on another role):</p>
              <ActorSearchList actors={avail} onSelect={assign} emptyText="No available actors for this date." testidPrefix="roster-actor" />
              <label className="flex items-center gap-2 mt-4 pt-4 border-t border-[#1a1a1a] cursor-pointer" data-testid="roster-send-email">
                <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-blood" />
                <span className="text-xs text-[#A19D94]">Send the role-assignment email</span>
              </label>
              <TempCoverPanel roleId={cell.role.id} showDateId={cell.showDate.id} onDone={() => { closeCell(); load(); }} />
            </>
          )}
        </ModalShell>
      )}

      {hover && createPortal(
        <div
          data-testid="roster-actor-popover"
          className="fixed z-[60] w-80 -translate-x-1/2 border border-[#2a2a2a] bg-[#0A0A0A] shadow-2xl shadow-black/60 p-4 pointer-events-none"
          style={{ left: hover.x, top: hover.y }}
        >
          <div className="flex items-center gap-4">
            {hover.actor.headshot_file_id ? (
              <img src={fileUrl(hover.actor.headshot_file_id, { thumb: true })} alt={hover.actor.full_name} loading="lazy" decoding="async" className="w-28 h-28 object-cover border border-[#262626] shrink-0 bg-[#1a1a1a]" />
            ) : (
              <div className="w-28 h-28 bg-[#1a1a1a] border border-[#262626] flex items-center justify-center shrink-0"><User size={40} className="text-[#6B6862]" /></div>
            )}
            <div className="min-w-0">
              <div className="font-display uppercase tracking-tight text-lg leading-tight">{hover.actor.full_name}</div>
              <div className="text-sm text-[#A19D94] mt-1.5">
                {ageFromDob(hover.actor.date_of_birth) !== null ? `Age ${ageFromDob(hover.actor.date_of_birth)}` : ""}
                {ageFromDob(hover.actor.date_of_birth) !== null && hover.actor.gender ? " · " : ""}
                {hover.actor.gender || ""}
                {hover.actor.is_temp_cover ? `Temporary cover${hover.actor.phone ? ` · ${hover.actor.phone}` : ""}` : (!hover.actor.date_of_birth && !hover.actor.gender ? "No details on file" : "")}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </AdminLayout>
  );
}

const Legend = ({ color, label }) => (
  <span className="inline-flex items-center gap-2"><span className="w-3 h-3" style={{ background: color }} /><span className="text-[#A19D94]">{label}</span></span>
);
